import { Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { socialKeysNotSetUp } from '@mg/shared';
import { useIsTenantAdmin } from '../../auth/useWorkspaceAdmin';
import { cn } from '../../lib/cn';
import { TOUCH_TARGET } from '../../components/ui/touchTarget';
import { SOCIAL_APPS_LINK } from './socialAppKeys';

/**
 * "<name> API connection hasn't been set up yet.": said wherever a connection cannot start because the
 * install has no app keys for it, beside the disabled action it explains. That action points at the
 * sentence (`aria-describedby={id}`), so the id is on the sentence itself.
 *
 * A Tenant Admin reads the "add the app keys" sentence and gets a link straight to the Social Media
 * Integrations card; anyone else is told to ask their administrator. That is the install-wide Tenant
 * Admin flag (useIsTenantAdmin), never the workspace admin: only a Tenant Admin can add the keys.
 *
 * No live role: the page can show several of these at once, and each would be announced on load.
 */
export function NotSetUpNotice({
  id,
  name,
  touch,
  row,
  className,
}: {
  /** The sentence's id, for the disabled action's `aria-describedby`. */
  id: string;
  /** Who is not set up: "Facebook", "Instagram", "LinkedIn Community Management", … */
  name: string;
  /** In a dialog: the link is a 44px touch target. */
  touch?: boolean;
  /**
   * Takes a whole row of the Connections page (a platform's group, where "Add Additional" goes): that
   * row's rounder, roomier shape. A shape of its own rather than classes laid over this one: `cn` only
   * joins classes, and the stylesheet orders one property's utilities by name, so a `rounded-2xl`
   * passed in would lose to the `rounded-lg` here.
   */
  row?: boolean;
  /** Spacing from what sits around it. */
  className?: string;
}) {
  const isTenantAdmin = useIsTenantAdmin();
  return (
    <div
      className={cn(
        'flex items-start gap-2 border border-amber-200 bg-amber-50 dark:border-warning-dark/30 dark:bg-warning-dark-tint',
        row ? 'rounded-2xl px-5 py-3' : 'rounded-lg px-3 py-2',
        className,
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 dark:text-warning-dark" aria-hidden />
      <p className="text-sm text-amber-800 dark:text-warning-dark">
        <span id={id}>{socialKeysNotSetUp(name, isTenantAdmin ? 'admin' : 'member')}</span>
        {isTenantAdmin && (
          <>
            {' '}
            <Link
              to={SOCIAL_APPS_LINK}
              className={cn(
                'font-semibold text-amber-900 underline underline-offset-2 dark:text-warning-dark',
                touch && `inline-flex items-center ${TOUCH_TARGET}`,
              )}
            >
              Open Site Integrations
            </Link>
          </>
        )}
      </p>
    </div>
  );
}
